/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * operatorWsClient.ts — operato\1 \2\1 \2lient (services/operator-web-app).
 */

import type { BaseEnvelope, ServerEnvelope } from './types';
import { parseServerEnvelope } from './parseEnvelope';

const MIN_RECONNECT_MS = 1000;
const MAX_RECONNECT_MS = 30000;
const BACKOFF_MULTIPLIER = 1.5;

export type OperatorWsClientListener = (event: ServerEnvelope) => void;
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface OperatorWsClientConfig {
  url: string;
  onConnectionState?: (state: ConnectionState) => void;
  onMessage?: OperatorWsClientListener;
}

export class OperatorWsClient {
  private url: string;
  private ws: WebSocket | null = null;
  private listeners = new Set<OperatorWsClientListener>();
  private onConnectionState?: (state: ConnectionState) => void;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;

  constructor(config: OperatorWsClientConfig) {
    this.url = config.url;
    this.onConnectionState = config.onConnectionState;
    if (config.onMessage) this.listeners.add(config.onMessage);
  }

  subscribe(listener: OperatorWsClientListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    this.intentionalClose = false;
    this.setState('connecting');
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.setState('disconnected');
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.setState('connected');
    };
    this.ws.onclose = () => {
      this.ws = null;
      this.setState('disconnected');
      if (!this.intentionalClose) this.scheduleReconnect();
    };
    this.ws.onerror = () => {
      // Close will follow; avoid double reconnect
    };
    this.ws.onmessage = (ev: MessageEvent) => {
      const envelope = parseServerEnvelope(ev.data as string);
      if (envelope) this.listeners.forEach((fn) => fn(envelope));
    };
  }

  disconnect(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setState('disconnected');
  }

  send(envelope: BaseEnvelope): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(envelope));
  }

  getConnectionState(): ConnectionState {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      default:
        return 'disconnected';
    }
  }

  private setState(state: ConnectionState): void {
    this.onConnectionState?.(state);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.intentionalClose) return;
    const delay = Math.min(
      MIN_RECONNECT_MS * Math.pow(BACKOFF_MULTIPLIER, this.reconnectAttempt),
      MAX_RECONNECT_MS
    );
    this.reconnectAttempt += 1;
    this.setState('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
